import { Cloud, Gauge } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import { SectionCard } from "../dashboard/DashboardPrimitives";
import { STATE_VARIANT } from "./badges";
import { formatAge, formatDuration } from "./fleetSummary";
import { echoPath, formatShare, windowLabel } from "./fleetWindow";
import {
  describeWorkerState,
  type H3Fleet,
  type H3History,
  type H3Job,
  type H3WorkerHistory,
  type SelfHostedWorker,
} from "./h3Fleet";

function Stat({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: ReactNode;
  sublabel?: ReactNode;
}) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-semibold tabular-nums">{value}</dd>
      {sublabel ? (
        <dd className="text-xs text-muted-foreground">{sublabel}</dd>
      ) : null}
    </div>
  );
}

/** What the box is doing this second: the clip it renders (and for how long) or its state. */
function NowLine({
  worker,
  job,
  now,
}: {
  worker: SelfHostedWorker | null;
  job: H3Job | null;
  now: Date;
}) {
  if (job) {
    const since =
      job.last_event === "claimed" ? job.last_event_at : job.dispatched_at;
    const elapsed = (now.getTime() - Date.parse(since)) / 1000;
    return (
      <p className="text-sm">
        rendering{" "}
        {job.workload_id ? (
          <Link
            to={echoPath(job.workload_id)}
            className="font-mono underline underline-offset-4"
            title={`Open Echo ${job.workload_id}`}
          >
            {job.workload_id.slice(0, 8)}
          </Link>
        ) : (
          <span className="font-mono">{job.job_id.slice(0, 8)}</span>
        )}{" "}
        for <span className="tabular-nums">{formatDuration(elapsed)}</span>
      </p>
    );
  }
  if (!worker) {
    return (
      <p className="text-sm text-muted-foreground">
        rendered in this window, not in the registry now
      </p>
    );
  }
  if (worker.state === "busy" && worker.current_job_id) {
    return (
      <p className="text-sm">
        rendering{" "}
        <span className="font-mono">{worker.current_job_id.slice(0, 8)}</span>
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      {describeWorkerState(worker.state)}
    </p>
  );
}

function WorkerCard({
  workerId,
  worker,
  stats,
  job,
  now,
  label,
  historyKnown,
}: {
  workerId: string;
  worker: SelfHostedWorker | null;
  stats: H3WorkerHistory | null;
  job: H3Job | null;
  now: Date;
  label: string;
  historyKnown: boolean;
}) {
  const state = worker?.state ?? "gone";
  const dash = historyKnown ? 0 : null;
  const clips = stats?.clips ?? dash;
  const share = stats?.busy_share ?? (historyKnown ? 0 : null);
  return (
    <div
      className={cn(
        "rounded-xl border bg-card p-4 text-card-foreground shadow-xs",
        state === "busy" && "border-emerald-500/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-mono text-sm font-semibold">{workerId}</p>
          <p className="truncate text-xs text-muted-foreground">
            {worker?.gpu ?? stats?.gpu ?? "GPU unknown"}
          </p>
        </div>
        <Badge variant={STATE_VARIANT[state] ?? "outline"}>{state}</Badge>
      </div>
      <div className="mt-3">
        <NowLine worker={worker} job={job} now={now} />
      </div>
      <dl className="mt-4 grid grid-cols-3 gap-3">
        <Stat
          label={`Clips ${label}`}
          value={clips ?? "—"}
          sublabel={
            stats
              ? `${stats.completed} ok${stats.failed ? ` · ${stats.failed} failed` : ""}`
              : undefined
          }
        />
        <Stat
          label="Avg gen"
          value={formatDuration(stats?.avg_gen_seconds ?? null)}
          sublabel={
            stats?.p50_gen_seconds != null
              ? `p50 ${formatDuration(stats.p50_gen_seconds)}`
              : undefined
          }
        />
        <Stat
          label="Last clip"
          value={
            stats?.last_clip_at
              ? formatAge(
                  (now.getTime() - Date.parse(stats.last_clip_at)) / 1000,
                )
              : "—"
          }
        />
      </dl>
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>GPU busy with H3, {label}</span>
          <span className="tabular-nums">{formatShare(share)}</span>
        </div>
        <Progress
          value={share === null ? 0 : Math.min(100, share * 100)}
          className="mt-1 h-1.5"
          aria-label={`GPU busy share, last ${label}`}
        />
      </div>
    </div>
  );
}

function RunPodCard({
  rows,
  now,
  label,
  historyKnown,
}: {
  rows: H3WorkerHistory[];
  now: Date;
  label: string;
  historyKnown: boolean;
}) {
  const clips = rows.reduce((sum, row) => sum + row.clips, 0);
  const completed = rows.reduce((sum, row) => sum + row.completed, 0);
  const failed = rows.reduce((sum, row) => sum + row.failed, 0);
  const genTotal = rows.reduce((sum, row) => sum + row.gen_seconds_total, 0);
  const workers = new Set(rows.map((row) => row.worker_id).filter(Boolean))
    .size;
  const lastClip = rows
    .map((row) => row.last_clip_at)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1);
  return (
    <div className="rounded-xl border border-dashed bg-card p-4 text-card-foreground shadow-xs">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Cloud className="size-4 text-muted-foreground" aria-hidden />
            RunPod (rented)
          </p>
          <p className="text-xs text-muted-foreground">
            {historyKnown
              ? `${workers} worker${workers === 1 ? "" : "s"} rendered for you`
              : "history unavailable"}
          </p>
        </div>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        takes what your GPUs cannot take at once
      </p>
      <dl className="mt-4 grid grid-cols-3 gap-3">
        <Stat
          label={`Clips ${label}`}
          value={historyKnown ? clips : "—"}
          sublabel={
            historyKnown
              ? `${completed} ok${failed ? ` · ${failed} failed` : ""}`
              : undefined
          }
        />
        <Stat
          label="Avg gen"
          value={formatDuration(completed > 0 ? genTotal / completed : null)}
        />
        <Stat
          label="Last clip"
          value={
            lastClip
              ? formatAge((now.getTime() - Date.parse(lastClip)) / 1000)
              : "—"
          }
        />
      </dl>
    </div>
  );
}

/**
 * One card per self-hosted GPU: what it renders right now and what it did in the
 * window (clips, generation time, share of the window it was busy), next to the
 * rented capacity it competes with. Registry rows say "now"; the history says
 * "in the window"; a box in one but not the other still gets a card.
 */
export function WorkerStatsCards({
  fleet,
  history,
  inFlight,
  now,
  windowHours,
}: {
  fleet: H3Fleet;
  history: H3History | null;
  inFlight: H3Job[];
  now: Date;
  windowHours: number;
}) {
  const label = windowLabel(windowHours);
  const historyKnown = Boolean(history && !history.error);
  const byWorker = history?.by_worker ?? [];
  const ownRows = byWorker.filter(
    (row) => row.plane === "self_hosted" && row.worker_id,
  );
  const registry = fleet.self_hosted.workers;
  const ids = Array.from(
    new Set([
      ...registry.map((worker) => worker.worker_id),
      ...ownRows.map((row) => row.worker_id as string),
    ]),
  );
  return (
    <SectionCard
      title={`Self-hosted GPUs, last ${label}`}
      icon={<Gauge className="size-4" />}
      action={
        history?.error ? (
          <span className="text-xs text-destructive">
            history unavailable: {history.error}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            now, and what each box rendered in the window
          </span>
        )
      }
    >
      {ids.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No self-hosted worker is enrolled or rendered in this window; every H3
          clip goes to RunPod.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {ids.map((id) => (
            <WorkerCard
              key={id}
              workerId={id}
              worker={
                registry.find((worker) => worker.worker_id === id) ?? null
              }
              stats={ownRows.find((row) => row.worker_id === id) ?? null}
              job={inFlight.find((job) => job.worker_id === id) ?? null}
              now={now}
              label={label}
              historyKnown={historyKnown}
            />
          ))}
          <RunPodCard
            rows={byWorker.filter((row) => row.plane === "runpod")}
            now={now}
            label={label}
            historyKnown={historyKnown}
          />
        </div>
      )}
    </SectionCard>
  );
}
